'use client' // This is the most important change!

import React, { useState, useEffect } from 'react'

import clsx from 'clsx'
import { useMediaQuery } from '@/hooks/useMediaQuery'
import { Icon } from '@/ui/atoms/Icon'
import { Button, ButtonContent } from '@/ui/atoms/Button'
import { Media } from '@/payload-types'
import { useTranslations } from 'next-intl'
import { useDownloadGate } from '@/lib/downloads/useDownloadGate'
import { useMemberFileHref } from '@/lib/downloads/useFilePass'
import { trackEvent } from '@/lib/analytics/posthog'
import { recordMemberActivity } from '@/lib/analytics/memberActivity'

export interface PdfCardProps {
  pdfFiles: string | Media
  fileTitle: string
  fileDescription?: string | null
  id?: string | null
  key: React.Key
  /** Knowledge Hub entry title, carried into the download notification email. */
  entryTitle?: string
}

// This is the component that will render on the client
const PdfCardImplementation = ({
  pdfFiles,
  fileTitle,
  fileDescription,
  key,
  entryTitle,
}: PdfCardProps) => {
  const t = useTranslations('public')
  const isMobile = useMediaQuery('(max-width: 768px)')
  // Downloads are members-only across the platform (vendor leads); logged-out
  // visitors see the sheet listed but every action leads to /login?next=<here>.
  // Until the session is known the card offers no action at all: a member tapping during
  // hydration used to be sent to the login page (2026-10-01).
  const { user, isLoaded, loginHref, notifyDownload } = useDownloadGate()
  // Members' links carry the file pass, so the file also opens when the phone hands it to
  // a different browser (see lib/downloads/filePass.ts).
  const fileHref = useMemberFileHref((pdfFiles as Media)?.url)
  const WrapperComponent = isMobile ? 'a' : 'div'
  const pathname = typeof window !== 'undefined' ? window.location.pathname : ''
  // member_activity target (EA's own record of signed-in members): the sheet, never a person
  const sheetTarget = fileTitle || (pdfFiles as Media)?.url || undefined

  const handleDownloadClick = (action: 'view' | 'download' = 'download') => {
    if (!user) return
    notifyDownload({
      action,
      pdfFilename: (pdfFiles as Media)?.filename || 'unknown.pdf',
      pdfUrl: (pdfFiles as Media)?.url || undefined,
      entryTitle,
    })
  }

  const wrapperProps = {
    className: clsx(
      'flex gap-16 p-4 bg-bg-subtle-blue rounded-md h-[80px]',
      isMobile && ['cursor-pointer'],
    ),
    href: isMobile && isLoaded ? (user ? fileHref : loginHref) : undefined,
    target: user ? '_blank' : undefined,
    rel: 'noopener noreferrer',
    'aria-label': isMobile ? `View details for ${fileTitle}` : undefined,
    onClick: isMobile && isLoaded ? () => { handleDownloadClick(); trackEvent('product_sheet_viewed', { file_title: fileTitle, file_url: (pdfFiles as Media)?.url, page_path: pathname, gated: !user }); void recordMemberActivity('sheet_view', { target: sheetTarget }) } : undefined,
  }

  // The rest of the component is the same as the previous Tailwind example...
  return (
    <WrapperComponent {...wrapperProps} key={key}>
      <div className="flex items-center gap-4" key={key}>
        <div>
          <Icon className={'!size-6'} name={'file'} />
        </div>
        <div className="flex flex-col">
          <span className="!text-body-M-semibold">{fileTitle}</span>
          {fileDescription && (
            <span className="!text-body-XS-regular !text-text-faded !m-0">{fileDescription}</span>
          )}
        </div>
      </div>
      {isMobile || !isLoaded ? null : user ? (
        <div className="flex flex-col items-start" key={`${key}-actions`}>
          <Button
            asChild
            variant={'ghost'}
            size={'mini'}
            className="!p-0 !no-underline hover:!underline"
          >
            <a
              href={fileHref}
              target="_blank"
              rel="noopener noreferrer"
              className={'flex'}
              onClick={() => { handleDownloadClick('view'); trackEvent('product_sheet_viewed', { file_title: fileTitle, file_url: (pdfFiles as Media)?.url, page_path: pathname }); void recordMemberActivity('sheet_view', { target: sheetTarget }) }}
            >
              <div className="flex gap-2 flex-row justify-center items-center">
                <Icon className={'size-4'} name={'eye-fill'} />
                {t('view')}
              </div>
            </a>
          </Button>
          <Button
            asChild
            variant={'ghost'}
            size={'mini'}
            className="!p-0 !no-underline hover:!underline"
          >
            <a
              href={fileHref}
              target="_blank"
              rel="noopener noreferrer"
              className="flex"
              onClick={() => { handleDownloadClick(); trackEvent('product_sheet_downloaded', { file_title: fileTitle, file_url: (pdfFiles as Media)?.url, page_path: pathname }); void recordMemberActivity('sheet_download', { target: sheetTarget }) }}
            >
              <div className="flex gap-2 flex-row justify-center items-center">
                <Icon className={'size-4'} name={'download-2-line'} />
                {t('download')}
              </div>
            </a>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col items-start justify-center" key={`${key}-actions`}>
          <Button
            asChild
            variant={'ghost'}
            size={'mini'}
            className="!p-0 !no-underline hover:!underline"
          >
            <a
              href={loginHref}
              className="flex"
              onClick={() => trackEvent('product_sheet_login_gate_click', { file_title: fileTitle, page_path: pathname })}
            >
              <div className="flex gap-2 flex-row justify-center items-center">
                <Icon className={'size-4'} name={'lock-fill'} />
                Sign in to view & download
              </div>
            </a>
          </Button>
        </div>
      )}
    </WrapperComponent>
  )
}

// --- Hydration-Safe Wrapper ---
// This is the component you will actually import into your pages.
export const PdfCard = (props: PdfCardProps) => {
  const [hasMounted, setHasMounted] = useState(false)

  useEffect(() => {
    setHasMounted(true)
  }, [])

  // On the server and during the initial client render, render a placeholder or nothing.
  // This avoids the hydration mismatch.
  if (!hasMounted) {
    // You could return a skeleton loader here for better UX
    return (
      <div className="flex flex-col rounded-lg overflow-hidden shadow-lg bg-white border border-gray-200 animate-pulse">
        <div className="w-full h-44 bg-gray-300"></div>
        <div className="p-4">
          <div className="h-6 w-3/4 bg-gray-300 rounded"></div>
          <div className="mt-4 h-4 w-full bg-gray-300 rounded"></div>
          <div className="mt-2 h-4 w-5/6 bg-gray-300 rounded"></div>
        </div>
      </div>
    )
  }

  // Once mounted on the client, render the actual interactive component.
  return <PdfCardImplementation {...props} />
}
